'use client';

import { memo, type FC } from 'react';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import translations from './LegalSection.translations';
import { tmaActions } from '../../../bootstrap/tmaActions';
import { useHost, useRuntime } from '../../../runtime/RuntimeContext';
import { isCsShotUzHost } from '../../../runtime/host';
import { getLegalLinks } from '../../../utils/legalLinks';
import { useLocalizedHref } from '../../../hooks/useLocalizedHref';

const linkClassName =
  'text-label-tertiary text-[10px] font-normal cursor-pointer no-underline transition-colors duration-200 ease-smooth hover:text-label-secondary active:text-label-secondary';

const LegalSectionComponent: FC = () => {
  const { t } = useLocalTranslation(translations);
  const host = useHost();
  const links = getLegalLinks(host);
  const isUz = isCsShotUzHost(host);
  const isWeb = useRuntime() === 'web';
  const localize = useLocalizedHref();

  const handleLegalLinkClick = (url: string) => {
    void tmaActions.openLink(url);
  };

  return (
    <div className="flex flex-col items-center gap-1 mt-10">
      <span className={linkClassName} onClick={() => handleLegalLinkClick(links.userAgreement)}>
        {t('userAgreement')}
      </span>
      <span className={linkClassName} onClick={() => handleLegalLinkClick(links.privacyPolicy)}>
        {t('privacyPolicy')}
      </span>
      {links.refundPolicy && (
        <span className={linkClassName} onClick={() => handleLegalLinkClick(links.refundPolicy!)}>
          {t('refundPolicy')}
        </span>
      )}
      {links.paymentOrder && (
        <span className={linkClassName} onClick={() => handleLegalLinkClick(links.paymentOrder!)}>
          {t('paymentOrder')}
        </span>
      )}
      {isUz && isWeb && (
        <a className={linkClassName} href={localize('/how-it-works')}>
          {t('howItWorks')}
        </a>
      )}
      {isUz && isWeb && (
        <a className={linkClassName} href={localize('/contacts')}>
          {t('contacts')}
        </a>
      )}
      {isUz && isWeb && (
        <a className={linkClassName} href={localize('/support')}>
          {t('support')}
        </a>
      )}
      {isUz && isWeb && (
        <a className={linkClassName} href={localize('/refund-terms')}>
          {t('refundTerms')}
        </a>
      )}
      {isUz && isWeb && (
        <a className={linkClassName} href={localize('/digital-content')}>
          {t('digitalContent')}
        </a>
      )}
      {!isUz && (
        <p className="text-label-tertiary text-[10px] font-normal leading-normal text-center whitespace-pre-line mt-3">
          {t('companyInfo')}
        </p>
      )}
      {isUz && (
        <div className="flex items-center justify-center gap-2 mt-3">
          <span className="inline-flex items-center rounded-md bg-white px-2 py-1">
            <img src="/paylogos/uzcard.png" alt="UzCard" className="h-4 w-auto" />
          </span>
          <span className="inline-flex items-center rounded-md bg-white px-2 py-1">
            <img src="/paylogos/humo.png" alt="Humo" className="h-4 w-auto" />
          </span>
        </div>
      )}
    </div>
  );
};

export const LegalSection = memo(LegalSectionComponent);
